import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatDate, daysUntil } from "../lib/api";
import { MODULE_SCHEMAS } from "../lib/moduleSchemas";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  ShieldCheck, AlertTriangle, Clock, Sparkles, ArrowRight, Check,
  RefreshCw, CalendarCheck2, FileWarning, Users, PartyPopper,
} from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { key: "nominees", title: "Nominees", icon: Users },
  { key: "expiring", title: "Expiring Documents", icon: FileWarning },
  { key: "stale", title: "Stale Entries", icon: Clock },
  { key: "done", title: "Complete", icon: PartyPopper },
];

export default function YearlyReview() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);

  // Action counters
  const [actions, setActions] = useState({ nominee: 0, doc: 0, stale: 0 });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/yearly-review/status");
      setStatus(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const complete = async () => {
    await api.post("/yearly-review/complete");
    toast.success("Yearly review recorded");
    setTimeout(() => navigate("/"), 600);
  };

  if (loading || !status) {
    return <div className="py-20 text-center text-slate-500 text-sm">Loading review...</div>;
  }

  const allClean =
    status.totals.nominee_missing === 0 &&
    status.totals.nominee_unverified === 0 &&
    status.totals.expiring_documents === 0 &&
    status.totals.stale_entries === 0;

  const progressPct = Math.round((step / (STEPS.length - 1)) * 100);

  return (
    <div className="space-y-6" data-testid="yearly-review-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-overline">Annual Vault Health Check</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-1">
            Yearly Review
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            A 5-minute guided flow to fix nominees, review expiring documents, and confirm
            stale entries. Completing it bumps your Emergency Readiness score.
          </p>
          {status.last_review_at && (
            <div className="mt-3 text-xs text-slate-500">
              Last review: <span className="font-medium tabular-nums">{formatDate(status.last_review_at)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Progress rail */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex gap-4 text-sm">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.key} className="flex items-center gap-2">
                  <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold
                    ${i < step ? "bg-emerald-600 text-white"
                      : i === step ? "bg-slate-900 text-white"
                      : "bg-slate-200 text-slate-500"}`}>
                    {i < step ? <Check className="h-4 w-4" /> : <Icon className="h-3.5 w-3.5" />}
                  </div>
                  <span className={`${i === step ? "font-medium text-slate-900" : "text-slate-500"} hidden sm:inline`}>
                    {s.title}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="text-xs tabular-nums text-slate-500">{progressPct}%</div>
        </div>
        <Progress value={progressPct} className="h-1.5" />
      </Card>

      {step === 0 && (
        <NomineeStep
          status={status}
          onFix={() => setActions((a) => ({ ...a, nominee: a.nominee + 1 }))}
          reload={load}
          next={() => setStep(1)}
        />
      )}

      {step === 1 && (
        <ExpiringDocsStep
          items={status.expiring_documents}
          onAction={() => setActions((a) => ({ ...a, doc: a.doc + 1 }))}
          reload={load}
          back={() => setStep(0)}
          next={() => setStep(2)}
        />
      )}

      {step === 2 && (
        <StaleEntriesStep
          items={status.stale_entries}
          onAction={() => setActions((a) => ({ ...a, stale: a.stale + 1 }))}
          reload={load}
          back={() => setStep(1)}
          next={() => setStep(3)}
        />
      )}

      {step === 3 && (
        <CompleteStep
          actions={actions}
          allClean={allClean}
          onComplete={complete}
          back={() => setStep(2)}
        />
      )}
    </div>
  );
}

/* ---------- Step 1: Nominees ---------- */

function NomineeStep({ status, onFix, reload, next }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nominee: "", nominee_relationship: "", nominee_verified: false });
  const [busy, setBusy] = useState(false);

  const startEdit = (r) => {
    setEditing(r.entity_id);
    setForm({
      nominee: r.nominee || "",
      nominee_relationship: r.nominee_relationship || "",
      nominee_verified: !!r.nominee_verified,
    });
  };

  const save = async (r) => {
    setBusy(true);
    try {
      const { data: current } = await api.get(`/entities/${r.module}/${r.entity_id}?mask=false`);
      await api.put(`/entities/${r.module}/${r.entity_id}`, {
        data: {
          ...current.data,
          nominee: form.nominee,
          nominee_relationship: form.nominee_relationship,
          nominee_verified: form.nominee_verified,
        },
      });
      toast.success("Nominee updated");
      onFix();
      setEditing(null);
      await reload();
    } catch {
      toast.error("Save failed");
    } finally { setBusy(false); }
  };

  const verifyInline = async (r) => {
    setBusy(true);
    try {
      const { data: current } = await api.get(`/entities/${r.module}/${r.entity_id}?mask=false`);
      await api.put(`/entities/${r.module}/${r.entity_id}`, {
        data: { ...current.data, nominee_verified: true },
      });
      toast.success("Verified");
      onFix();
      await reload();
    } finally { setBusy(false); }
  };

  const missing = status.nominee_missing;
  const unverified = status.nominee_unverified;

  return (
    <>
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-1">
          <AlertTriangle className="h-5 w-5 text-amber-600" strokeWidth={1.75} />
          <h2 className="font-heading text-xl font-semibold">Step 1 · Nominees</h2>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          {missing.length === 0 && unverified.length === 0
            ? "All tracked accounts have verified nominees. ✓"
            : `${missing.length} missing, ${unverified.length} unverified — fix them inline below.`}
        </p>

        {missing.length > 0 && (
          <>
            <div className="label-overline mt-2 mb-2">Missing nominees ({missing.length})</div>
            <ul className="divide-y divide-slate-100" data-testid="yr-missing-list">
              {missing.map((r) => (
                <li key={r.entity_id} className="py-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-medium">{r.title}</div>
                      <div className="text-xs text-slate-500">
                        {MODULE_SCHEMAS[r.module]?.label} · {r.institution}
                      </div>
                    </div>
                    {editing !== r.entity_id && (
                      <Button size="sm" variant="outline" onClick={() => startEdit(r)}
                        data-testid={`yr-fix-${r.entity_id}`}>
                        Fix
                      </Button>
                    )}
                  </div>
                  {editing === r.entity_id && (
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
                      <Input placeholder="Nominee name" value={form.nominee}
                        onChange={(e) => setForm({ ...form, nominee: e.target.value })} />
                      <Input placeholder="Relationship" value={form.nominee_relationship}
                        onChange={(e) => setForm({ ...form, nominee_relationship: e.target.value })} />
                      <div className="flex items-center justify-between gap-2">
                        <label className="inline-flex items-center gap-2 text-xs">
                          <Checkbox checked={form.nominee_verified}
                            onCheckedChange={(v) => setForm({ ...form, nominee_verified: !!v })} />
                          Verified
                        </label>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                          <Button size="sm" onClick={() => save(r)} disabled={busy}
                            data-testid={`yr-save-${r.entity_id}`}>Save</Button>
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}

        {unverified.length > 0 && (
          <>
            <div className="label-overline mt-4 mb-2">Unverified nominees ({unverified.length})</div>
            <ul className="divide-y divide-slate-100" data-testid="yr-unverified-list">
              {unverified.map((r) => (
                <li key={r.entity_id} className="py-3 flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-medium">{r.title}</div>
                    <div className="text-xs text-slate-500">
                      Nominee: <span className="text-slate-700">{r.nominee}</span> ·
                      {" "}{r.nominee_relationship || "no relation set"}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => startEdit(r)}>Edit</Button>
                    <Button size="sm" onClick={() => verifyInline(r)} disabled={busy}
                      data-testid={`yr-verify-${r.entity_id}`}>
                      <ShieldCheck className="h-4 w-4 mr-1" /> Mark verified
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>

      <div className="flex justify-end">
        <Button onClick={next} data-testid="yr-next-nominees">
          Next · Expiring documents <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </>
  );
}

/* ---------- Step 2: Expiring docs ---------- */

function ExpiringDocsStep({ items, onAction, reload, back, next }) {
  const [busy, setBusy] = useState(null);

  const confirmOk = async (id) => {
    setBusy(id);
    try {
      await api.post(`/entities/documents/${id}/touch`);
      toast.success("Confirmed still valid");
      onAction();
      await reload();
    } finally { setBusy(null); }
  };

  return (
    <>
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-1">
          <FileWarning className="h-5 w-5 text-amber-600" strokeWidth={1.75} />
          <h2 className="font-heading text-xl font-semibold">Step 2 · Expiring Documents (180 days)</h2>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          {items.length === 0
            ? "No documents expiring soon. ✓"
            : `${items.length} documents expire within 180 days — confirm or renew each.`}
        </p>
        {items.length > 0 && (
          <ul className="divide-y divide-slate-100" data-testid="yr-expiring-list">
            {items.map((d) => {
              const days = daysUntil(d.expiry_date);
              return (
                <li key={d.entity_id} className="py-3 flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-medium">{d.title}</div>
                    <div className="text-xs text-slate-500">
                      {d.category} · expires {formatDate(d.expiry_date)}
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <Badge variant={days <= 30 ? "destructive" : "secondary"}>
                      {days <= 0 ? "Expired" : `${days} days`}
                    </Badge>
                    <Button size="sm" variant="outline"
                      onClick={() => window.open("/m/documents", "_self")}
                      data-testid={`yr-doc-open-${d.entity_id}`}>
                      Renew / Edit
                    </Button>
                    <Button size="sm" onClick={() => confirmOk(d.entity_id)}
                      disabled={busy === d.entity_id}
                      data-testid={`yr-doc-ok-${d.entity_id}`}>
                      <Check className="h-4 w-4 mr-1" /> Still valid
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
      <div className="flex justify-between">
        <Button variant="outline" onClick={back}>Back</Button>
        <Button onClick={next} data-testid="yr-next-expiring">
          Next · Stale entries <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </>
  );
}

/* ---------- Step 3: Stale entries ---------- */

function StaleEntriesStep({ items, onAction, reload, back, next }) {
  const [busy, setBusy] = useState(null);

  const confirm = async (r) => {
    setBusy(r.entity_id);
    try {
      await api.post(`/entities/${r.module}/${r.entity_id}/touch`);
      toast.success("Confirmed accurate");
      onAction();
      await reload();
    } finally { setBusy(null); }
  };

  return (
    <>
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-1">
          <Clock className="h-5 w-5 text-slate-700" strokeWidth={1.75} />
          <h2 className="font-heading text-xl font-semibold">Step 3 · Stale Entries (9+ months)</h2>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          {items.length === 0
            ? "Everything's fresh. Nothing has sat untouched for 9+ months. ✓"
            : `${items.length} entries haven't been touched in 9+ months. Confirm they're still accurate.`}
        </p>
        {items.length > 0 && (
          <ul className="divide-y divide-slate-100" data-testid="yr-stale-list">
            {items.map((r) => (
              <li key={r.entity_id} className="py-3 flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-medium">{r.title}</div>
                  <div className="text-xs text-slate-500">
                    {MODULE_SCHEMAS[r.module]?.label} · last updated {formatDate(r.updated_at)}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline"
                    onClick={() => window.open(`/m/${r.module}`, "_self")}>
                    Review
                  </Button>
                  <Button size="sm" onClick={() => confirm(r)}
                    disabled={busy === r.entity_id}
                    data-testid={`yr-stale-ok-${r.entity_id}`}>
                    <Check className="h-4 w-4 mr-1" /> Still accurate
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <div className="flex justify-between">
        <Button variant="outline" onClick={back}>Back</Button>
        <Button onClick={next} data-testid="yr-next-stale">
          Finish <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </>
  );
}

/* ---------- Step 4: Complete ---------- */

function CompleteStep({ actions, allClean, onComplete, back }) {
  const total = actions.nominee + actions.doc + actions.stale;
  return (
    <>
      <Card className="p-10 text-center">
        <div className="h-14 w-14 rounded-full bg-emerald-100 text-emerald-700 mx-auto flex items-center justify-center">
          <PartyPopper className="h-7 w-7" />
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold mt-4">
          {allClean && total === 0 ? "Everything looks healthy" : "Great work — you're covered for the year"}
        </h2>
        <p className="text-slate-600 text-sm mt-2 max-w-md mx-auto">
          You fixed <span className="font-medium">{actions.nominee}</span> nominee(s),
          confirmed <span className="font-medium">{actions.doc}</span> document(s) and
          refreshed <span className="font-medium">{actions.stale}</span> stale entries.
          <br />Click below to record the review — it bumps your Emergency Readiness score.
        </p>
        <div className="flex justify-center gap-3 mt-6">
          <Button variant="outline" onClick={back}>Back</Button>
          <Button onClick={onComplete} data-testid="yr-complete-button">
            <Sparkles className="h-4 w-4 mr-2" /> Mark review complete
          </Button>
        </div>
        <p className="text-xs text-slate-400 mt-6">
          We'll remind you to run this again in a year.
        </p>
      </Card>
    </>
  );
}
